import React, { useState } from "react";
import { styled } from "styled-components";
import Section from "@cs/components/src/Section";
import Input from "@cs/components/src/Input";
import Button from "@cs/components/src/Button";
import Spinner from "@cs/components/src/Spinner";
import ApiService, { sessionKey } from "../../services/ApiService";
import { useMutation, useQueryClient } from "react-query";
import Banner from "@cs/components/src/Banner";

const Wrapper = styled.div`
  max-width: 500px;
  width: 90%;
  margin: auto;
  margin-top: 50px;
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 10px;
  font-size: 15px;
  margin-bottom: 5px;
  text-align: center;
  color: ${({ error }) => (error ? "var(--red500)" : "var(--gray300)")};
`;

const RequestPinScreen = () => {
  const [email, setEmail] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [denied, setDenied] = useState(false);
  const queryClient = useQueryClient();

  type SessionResult = {
    status: string;
    id?: string;
    expires?: number;
    isAdmin?: boolean;
    email?: string;
  };

  const pollMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const response = await ApiService.fetch<{ data: SessionResult }>(
        "POST",
        "/api/session",
        { requestId }
      );
      return response.data;
    },
  });

  const pollForSession = (requestId: string) => {
    const deadline = Date.now() + 3 * 60 * 1000;

    const attempt = () => {
      if (Date.now() > deadline) {
        setWaiting(false);
        setDenied(true);
        return;
      }

      pollMutation.mutate(requestId, {
        onSuccess: (data) => {
          if (data.status === "approved" && data.id) {
            localStorage.setItem(sessionKey, JSON.stringify(data));
            queryClient.invalidateQueries(["session"]);
          } else if (data.status === "denied") {
            setWaiting(false);
            setDenied(true);
          } else {
            setTimeout(attempt, 1000);
          }
        },
        onError: () => {
          setWaiting(false);
          setDenied(true);
        },
      });
    };

    attempt();
  };

  const authMutation = useMutation({
    mutationFn: async () => {
      const response = await ApiService.fetch<{ data: { requestId: string } }>(
        "POST",
        "/api/session/auth",
        { email }
      );
      return response.data.requestId;
    },
    onSuccess: (requestId) => {
      setWaiting(true);
      setDenied(false);
      pollForSession(requestId);
    },
  });

  return (
    <Wrapper>
      <Section title="Iconik Manager" pad>
        {waiting ? (
          <>
            <Message>Waiting for Slack approval...</Message>
            <Spinner />
          </>
        ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            <Input
              label="Enter your CineSys email:"
              value={email}
              onChange={setEmail}
              onSubmit={() => authMutation.mutate()}
            />
            {denied && (
              <Banner
                error="Request was denied or expired. Please try again."
                padBottom
              />
            )}
            {authMutation.isError && (
              <Banner error="That email wasn't recognized." padBottom />
            )}
            <Button
              label="Authorize with Slack"
              type="success"
              disabled={!email}
              onClick={() => authMutation.mutate()}
              loading={authMutation.isLoading}
            />
          </form>
        )}
      </Section>
    </Wrapper>
  );
};

export default RequestPinScreen;
